/**
 * Auth 弹窗服务
 * 提供全局解耦的认证弹窗调用方式
 */
import { ModalService } from '@/components/moda-lInvoker';
import AuthContentModal from '@/components/auth-content/AuthContentModal';
import type { AuthStep } from '@/components/auth-content/types';
import { isMobile } from '@/utils/device';

/**
 * 打开认证弹窗的选项
 */
export interface OpenAuthModalOptions {
  /** 初始步骤 */
  step?: AuthStep;
  /** 初始邮箱 */
  email?: string;
  /** 邮件链接中的加密参数 */
  cs?: string;
  /** 关闭回调 */
  onClose?: () => void;
  /** 登录成功回调 */
  onLoginSuccess?: () => void;
  /** 注册成功回调 */
  onSignUpSuccess?: () => void;
}

/**
 * 认证弹窗服务
 * 使用 ModalService 命令式调用，全局解耦
 */
class AuthModalService {
  private currentModalPromise: Promise<unknown> | null = null;

  /**
   * 打开认证弹窗
   * @param options 弹窗选项
   * @returns Promise，resolve 表示认证成功，reject 表示取消
   */
  async open(options: OpenAuthModalOptions = {}): Promise<void> {
    const { step = 'login', email = '', cs, onClose, onLoginSuccess, onSignUpSuccess } = options;

    // 如果已有弹窗打开，直接返回
    if (this.currentModalPromise) {
      return this.currentModalPromise as Promise<void>;
    }

    const mobile = isMobile();

    // 动态导入 useAuth hook 的逻辑会在 AuthContentModal 内部处理
    // 这里只负责打开弹窗；移动端使用 form-only 变体且不可关闭
    this.currentModalPromise = ModalService.open(AuthContentModal, {
      step,
      email,
      cs,
      variant: mobile ? 'form-only' : 'full',
      showCloseButton: !mobile,
      closable: !mobile,
      onClose: () => {
        this.currentModalPromise = null;
        onClose?.();
      },
      onLoginSuccess: () => {
        this.currentModalPromise = null;
        onLoginSuccess?.();
      },
      onSignUpSuccess: () => {
        this.currentModalPromise = null;
        onSignUpSuccess?.();
      },
    });

    try {
      await this.currentModalPromise;
    } finally {
      this.currentModalPromise = null;
    }
  }

  /**
   * 打开登录弹窗
   */
  openLogin(options: Omit<OpenAuthModalOptions, 'step'> = {}) {
    return this.open({ ...options, step: 'login' });
  }

  /**
   * 打开注册弹窗
   */
  openSignUp(options: Omit<OpenAuthModalOptions, 'step'> = {}) {
    return this.open({ ...options, step: 'signup' });
  }

  /**
   * 打开忘记密码弹窗
   */
  openForgotPassword(options: Omit<OpenAuthModalOptions, 'step'> = {}) {
    return this.open({ ...options, step: 'forgot-password' });
  }

  /**
   * 打开邮件验证弹窗（邮件链接回调）
   * @param cs 邮件链接中的加密参数
   */
  openVerify(cs: string, options: Omit<OpenAuthModalOptions, 'step' | 'cs'> = {}) {
    return this.open({ ...options, step: 'verify-email', cs });
  }

  /**
   * 打开重置密码弹窗（邮件链接回调）
   * @param cs 邮件链接中的加密参数
   */
  openResetPassword(cs: string, options: Omit<OpenAuthModalOptions, 'step' | 'cs'> = {}) {
    return this.open({ ...options, step: 'reset-password', cs });
  }

  /**
   * 关闭当前认证弹窗
   */
  close() {
    if (this.currentModalPromise) {
      ModalService.closeAll();
      this.currentModalPromise = null;
    }
  }

  /**
   * 检查是否有弹窗正在打开
   */
  isOpen(): boolean {
    return this.currentModalPromise !== null;
  }
}

// 导出单例
export const authModalService = new AuthModalService();

export default authModalService;
